import { useState } from 'react';
import { useSecurityNotifications } from '../contexts/SecurityNotificationsContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Bell, BellRing, Shield, AlertTriangle, Check, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PRIORITY_STYLES = {
  urgent: {
    bg: 'bg-red-50',
    border: 'border-l-red-500',
    icon: 'text-red-500'
  },
  important: {
    bg: 'bg-orange-50',
    border: 'border-l-orange-500',
    icon: 'text-orange-500'
  }
};

export default function SecurityNotificationBell() {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    permission,
    scannerStatus,
    requestPermission,
    markAsRead,
    markAllAsRead
  } = useSecurityNotifications();
  
  const [open, setOpen] = useState(false);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setOpen(false);
    navigate('/security');
  };

  const recentNotifications = notifications.slice(0, 5);
  const hasUrgent = notifications.some(n => n.priority === 'urgent' && !n.read);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="security-notification-bell"
        >
          {hasUrgent ? (
            <BellRing className="w-5 h-5 text-red-500 animate-pulse" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-80" align="end">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-500" />
            <span>Alertes Sécurité</span>
          </div>
          {scannerStatus?.running && (
            <Badge variant="outline" className="text-xs">
              Auto: {scannerStatus.scan_interval_minutes}min
            </Badge>
          )}
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        {/* Permission notification */}
        {permission !== 'granted' && (
          <DropdownMenuItem onClick={requestPermission} className="cursor-pointer">
            <Volume2 className="w-4 h-4 mr-2 text-blue-500" />
            <span className="text-sm">Activer les notifications sonores</span>
          </DropdownMenuItem>
        )}
        
        {/* Mode silencieux indicator */}
        {scannerStatus?.silent_mode_active && (
          <div className="px-2 py-1 bg-gray-100 text-xs text-gray-600 flex items-center gap-2">
            <VolumeX className="w-3 h-3" />
            Mode silencieux actif (après 22h)
          </div>
        )}
        
        <DropdownMenuGroup>
          {recentNotifications.length > 0 ? (
            recentNotifications.map((notification) => {
              const styles = PRIORITY_STYLES[notification.priority] || PRIORITY_STYLES.important;
              return (
                <DropdownMenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`cursor-pointer border-l-4 ${styles.border} ${styles.bg} ${
                    notification.read ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-3 h-3 ${styles.icon}`} />
                      <span className="font-medium text-sm truncate">
                        {notification.title}
                      </span>
                      {!notification.read && (
                        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(notification.created_at).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </DropdownMenuItem>
              );
            })
          ) : (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              <Shield className="w-8 h-8 mx-auto mb-2 text-green-500" />
              Aucune alerte active
            </div>
          )}
        </DropdownMenuGroup>
        
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2 flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => {
                  markAllAsRead();
                }}
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Tout lu
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => {
                  setOpen(false);
                  navigate('/security');
                }}
              >
                Voir tout
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
